import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isAdmin: false,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

/**
 * True when the browser still holds a persisted Supabase auth token.
 *
 * After a tab has been backgrounded, `getSession()` can resolve with `null`
 * for a moment while the client refreshes an expired access token. Treating
 * that instant as "signed out" is what used to bounce admins out of the page
 * they were working on, so we look at the stored token before giving up.
 */
const hasPersistedSession = () => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("sb-") && key.endsWith("-auth-token") && localStorage.getItem(key)) {
        return true;
      }
    }
  } catch {
    /* storage blocked — fall through */
  }
  return false;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  // The admin role is fetched asynchronously AFTER the session resolves.
  // If we reported loading=false before it lands, every admin route would see
  // a signed-in-but-not-admin flash and bounce the user to /admin/login,
  // losing the page they asked for. Stay "loading" until the role is known.
  const [roleLoading, setRoleLoading] = useState(true);
  // Which user id the role check already ran for. Token refreshes re-emit the
  // same user; re-running the check (and flipping loading back on) would
  // unmount the whole admin subtree and destroy unsaved work.
  const checkedUserId = useRef<string | null>(null);

  const checkAdmin = async (userId: string) => {
    try {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
      checkedUserId.current = userId;
    } catch {
      // Network hiccup during a refresh must not revoke an already-granted
      // role — only clear it if we never had one.
      if (checkedUserId.current !== userId) setIsAdmin(false);
    } finally {
      setRoleLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const applySession = (next: Session | null) => {
      setSession(next);
      setUser(next?.user ?? null);
      if (next?.user) {
        if (checkedUserId.current !== next.user.id) {
          setRoleLoading(true);
          // Don't await inside the auth callback to avoid deadlocks.
          checkAdmin(next.user.id);
        } else {
          // Same user (token refresh / focus re-emit): keep the known role and
          // never re-enter a loading state that would remount admin pages.
          setRoleLoading(false);
        }
      } else {
        checkedUserId.current = null;
        setIsAdmin(false);
        setRoleLoading(false);
      }
      setAuthLoading(false);
    };

    // Restore the session from storage. If nothing comes back but a token is
    // still persisted, the access token is likely just expired — give the
    // client a chance to refresh before declaring the user signed out.
    const bootstrap = async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      if (data.session) {
        applySession(data.session);
        return;
      }
      if (!hasPersistedSession()) {
        applySession(null);
        return;
      }
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (!mounted) return;
      applySession(refreshed.session ?? null);
    };

    void bootstrap();

    // Then listen for auth changes (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, next) => {
        if (!mounted) return;
        // A refresh failure or a transient null during INITIAL_SESSION must not
        // tear down an established session; only an explicit sign-out does.
        if (!next && event !== "SIGNED_OUT" && hasPersistedSession()) return;
        applySession(next);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);


  const signOut = async () => {
    await supabase.auth.signOut();
    checkedUserId.current = null;
    setUser(null);
    setSession(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, loading: authLoading || roleLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
